import { inject, Injectable, signal } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { CookiesService } from './cookies';

interface DecodedToken {
  claims?: string[];
  exp?: number;
  role?: unknown;
  [key: string]: unknown;
}

/** When present on the JWT role claim, grants every `access_control.*` permission (admin SSO). */
const SPAY_SUPER_ADMIN_ROLE = 'spay_super_admin';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private _permissions = signal<string[]>([]);
  private _spaySuperAdminAccessControl = signal(false);
  private cookieService = inject(CookiesService);

  get permissions() {
    return this._permissions();
  }

  get isSuperAdmin(): boolean {
    return this._spaySuperAdminAccessControl();
  }

  hasPermission(permission: string) {
    if (
      permission.startsWith('access_control.') &&
      this._spaySuperAdminAccessControl()
    ) {
      return true;
    }
    return this._permissions().includes(permission);
  }

  hasAnyPermission(...permissions: string[]) {
    return permissions.some((p) => this.hasPermission(p));
  }

  setPermissions(permissions: string[] | null | undefined) {
    const list = Array.isArray(permissions)
      ? permissions.filter((p): p is string => typeof p === 'string')
      : [];
    this._permissions.set(list);
    this.refreshSpaySuperAdminAccessControlFromCookie();
  }

  private refreshSpaySuperAdminAccessControlFromCookie(): void {
    const token = this.cookieService.get('token');
    if (!token) {
      this._spaySuperAdminAccessControl.set(false);
      return;
    }
    try {
      const decoded: DecodedToken = jwtDecode(token);
      if (decoded.exp != null && Date.now() >= decoded.exp * 1000) {
        this._spaySuperAdminAccessControl.set(false);
        return;
      }
      const role = decoded.role;
      this._spaySuperAdminAccessControl.set(
        typeof role === 'string' &&
          role.toLowerCase() === SPAY_SUPER_ADMIN_ROLE,
      );
    } catch {
      this._spaySuperAdminAccessControl.set(false);
    }
  }

  loadPermissionsFromToken() {
    const token = this.cookieService.get('token');
    if (!token) {
      this._permissions.set([]);
      this.refreshSpaySuperAdminAccessControlFromCookie();
      return;
    }

    try {
      const decoded: DecodedToken = jwtDecode(token);
      if (decoded.exp && Date.now() >= decoded.exp * 1000) {
        this._permissions.set([]);
        this.refreshSpaySuperAdminAccessControlFromCookie();
        return;
      }
      const raw = decoded.claims;
      this._permissions.set(
        Array.isArray(raw)
          ? raw.filter((p): p is string => typeof p === 'string')
          : [],
      );
      this.refreshSpaySuperAdminAccessControlFromCookie();
    } catch {
      this._permissions.set([]);
      this.refreshSpaySuperAdminAccessControlFromCookie();
    }
  }

  clearPermissions() {
    this._permissions.set([]);
    this._spaySuperAdminAccessControl.set(false);
  }
}
